import React, { useMemo } from 'react'

import { Table, TableRow } from '../../../components/bulma/table.styled'
import { StickyTableRow } from '../../../components/sticky-table-row.styled'
import { formatDateTime } from '../../../lib/date-and-time'
import { ConsignmentsBookingCandidatesResponse } from '../../../webapi/hooks/queries/use-get-consignments-candidates'

import { BookConsignmentActionButton } from './book-consignment-action-button'
import { ActionButtons, ActionsHeaderColumn } from './consignments-booking.styled'
import { ExportActionButton } from './export-action-button'

interface Props {
  data: ConsignmentsBookingCandidatesResponse
  terminalNames: Record<string, string>
  className?: string
}

export const ConsignmentsBookingTable = ({ data, className, terminalNames }: Props) => {
  const unbookedInCountryTotal = useMemo(() => {
    return data.terminals
      .filter((terminal) => terminal.numberOfUnbookedInCountry != null && terminal.numberOfUnbookedInCountry >= 0)
      .map((terminal) => terminal.numberOfUnbookedInCountry ?? 0)
      .reduce((a, v) => a + v, 0)
  }, [data])

  const unbookedNotInCountryTotal = useMemo(() => {
    return data.terminals
      .filter((terminal) => terminal.numberOfUnbookedNotInCountry != null && terminal.numberOfUnbookedNotInCountry >= 0)
      .map((terminal) => terminal.numberOfUnbookedNotInCountry ?? 0)
      .reduce((a, v) => a + v, 0)
  }, [data])

  return (
    <Table className={className} isNarrow isFullwidth isStriped>
      <thead>
        <StickyTableRow>
          <th>Terminal</th>
          <th className="has-text-right">Candidates</th>
          <th className="has-text-right">Booked</th>
          <th className="has-text-right">{"Today's"} Limit</th>
          <th className="has-text-right">Unbooked in Country</th>
          <th className="has-text-right">Unbooked Not in Country</th>
          <th>Routing Deadline</th>
          <ActionsHeaderColumn>Actions</ActionsHeaderColumn>
        </StickyTableRow>
      </thead>
      <tbody>
        {data.terminals.map(
          ({
            terminalCode,
            numberOfCandidates,
            numberOfConsignmentsBooked,
            consignmentLimit,
            routingDeadline,
            locked,
            numberOfUnbookedInCountry,
            numberOfUnbookedNotInCountry,
          }) => (
            <TableRow key={terminalCode}>
              <td>{terminalNames[terminalCode]}</td>
              <td className="has-text-right">{numberOfCandidates}</td>
              <td className="has-text-right">{numberOfConsignmentsBooked}</td>
              <td className="has-text-right">
                {consignmentLimit < 0 ? <span className="tag is-danger is-light">Error</span> : consignmentLimit}
              </td>
              <td className="has-text-right">
                {numberOfUnbookedInCountry == null || numberOfUnbookedInCountry < 0 ? (
                  <span className="tag is-danger is-light">Error</span>
                ) : (
                  numberOfUnbookedInCountry ?? 'N/A'
                )}
              </td>
              <td className="has-text-right">
                {numberOfUnbookedNotInCountry == null || numberOfUnbookedNotInCountry < 0 ? (
                  <span className="tag is-danger is-light">Error</span>
                ) : (
                  numberOfUnbookedNotInCountry ?? 'N/A'
                )}
              </td>
              <td>{routingDeadline ? formatDateTime(routingDeadline) : 'N/A'}</td>
              <td>
                <ActionButtons>
                  <BookConsignmentActionButton
                    terminalNames={terminalNames}
                    locked={locked}
                    terminalCode={terminalCode}
                    disabled={consignmentLimit < 0}
                  />
                  <ExportActionButton terminalCode={terminalCode} />
                </ActionButtons>
              </td>
            </TableRow>
          )
        )}
      </tbody>
      {data.sum && (
        <tfoot>
          <tr>
            <th>Total</th>
            <td className="has-text-right">{data.sum.candidates}</td>
            <td className="has-text-right">{data.sum.booked}</td>
            <td className="has-text-right">{data.sum.consignmentLimit}</td>
            <td className="has-text-right">{unbookedInCountryTotal}</td>
            <td className="has-text-right">{unbookedNotInCountryTotal}</td>
            <td></td>
            <td></td>
          </tr>
        </tfoot>
      )}
    </Table>
  )
}
